import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import coursesApi, {
  CreateCourseRequest,
  UpdateCourseRequest,
  EnrollUserRequest,
  AssignDepartmentRequest,
} from '@/services/apis/courses';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';

// Query keys
export const courseKeys = {
  all: ['courses'] as const,
  lists: () => [...courseKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...courseKeys.lists(), filters] as const,
  details: () => [...courseKeys.all, 'detail'] as const,
  detail: (id: string) => [...courseKeys.details(), id] as const,
  enrollments: (courseId: string) => [...courseKeys.all, 'enrollments', courseId] as const,
  departmentAssignments: (courseId: string) => [...courseKeys.all, 'department-assignments', courseId] as const,
  currentUserEnrollment: (courseId: string, userId: string) =>
    [...courseKeys.all, 'current-user-enrollment', courseId, userId] as const,
  currentUserProgress: (courseId: string, userId: string) =>
    [...courseKeys.all, 'current-user-progress', courseId, userId] as const,
  guidesProgress: (courseId: string, userId: string) =>
    [...courseKeys.all, 'guides-progress', courseId, userId] as const,
  userResumeCourses: (language: string) => [...courseKeys.all, 'user', 'resume', language] as const,
  userRecommendedCourses: (language: string) => [...courseKeys.all, 'user', 'recommended', language] as const,
  userAssignedCourses: (language: string) => [...courseKeys.all, 'user', 'assigned', language] as const,
};

export interface CoursesFilters {
  limit?: number;
  categoryId?: string;
  categoryIds?: string[];
  isPublished?: boolean;
  isRecommended?: boolean;
  language?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  useDraftData?: boolean;
}

// Hooks for fetching courses with infinite scroll
export const useInfiniteCourses = (filters: CoursesFilters = {}) => {
  const {
    limit = 10,
    categoryId,
    categoryIds,
    isPublished,
    isRecommended,
    language = 'en',
    search,
    sortBy = 'createdAt',
    sortOrder = 'desc',
    useDraftData = false,
  } = filters;

  return useInfiniteQuery({
    queryKey: courseKeys.list({
      limit,
      categoryId,
      categoryIds,
      isPublished,
      isRecommended,
      language,
      search,
      sortBy,
      sortOrder,
      useDraftData,
    }),
    queryFn: async ({ pageParam }) => {
      const response = await coursesApi.getCourses({
        cursor: pageParam,
        limit,
        categoryId,
        categoryIds,
        isPublished,
        isRecommended,
        language,
        search,
        sortBy,
        sortOrder,
        useDraftData,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useCourse = ({ id, shouldSkip }: { id: string; shouldSkip: boolean }) => {
  return useQuery({
    queryKey: courseKeys.detail(id),
    queryFn: async () => {
      const response = await coursesApi.getCourse(id);
      return response.data!;
    },
    enabled: !!id && !shouldSkip,
  });
};

export const usePublishedCourseVersion = ({ id, shouldSkip }: { id: string; shouldSkip: boolean }) => {
  return useQuery({
    queryKey: [...courseKeys.detail(id), 'published-version'],
    queryFn: async () => {
      const response = await coursesApi.getPublishedVersion(id);
      return response.data!;
    },
    enabled: !!id && !shouldSkip,
  });
};

// Hooks for mutations
export const useCreateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseData: CreateCourseRequest) => coursesApi.createCourse(courseData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
};

export const useUpdateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCourseRequest }) => coursesApi.updateCourse(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
};

export const useDeleteCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => coursesApi.deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
};

export const usePublishCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => coursesApi.publishCourse(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
};

export const useUnpublishCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => coursesApi.unpublishCourse(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
};

export const useCourseEnrollments = (courseId: string) => {
  return useQuery({
    queryKey: courseKeys.enrollments(courseId),
    queryFn: async () => {
      const response = await coursesApi.getCourseEnrollments(courseId);
      return response.data!;
    },
    enabled: !!courseId,
  });
};

export const useEnrollUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');
  return useMutation({
    mutationFn: ({ courseId, userId, data }: { courseId: string; userId: string; data?: EnrollUserRequest }) =>
      coursesApi.enrollUser(courseId, userId, data),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.enrollments(courseId) });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      addToast({
        title: t('courses.assignUsers.enrollSuccess'),
        color: 'success',
      });
    },
    onError: (error) => {
      addToast({
        title: error.message || t('courses.assignUsers.enrollError'),
        color: 'danger',
      });
    },
  });
};

export const useUnenrollUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');
  return useMutation({
    mutationFn: ({ courseId, userId }: { courseId: string; userId: string }) =>
      coursesApi.unenrollUser(courseId, userId),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.enrollments(courseId) });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      addToast({
        title: t('courses.assignUsers.unenrollSuccess'),
        color: 'success',
      });
    },
    onError: (error) => {
      addToast({
        title: error.message || t('courses.assignUsers.unenrollError'),
        color: 'danger',
      });
    },
  });
};

export const useDepartmentAssignments = (courseId: string) => {
  return useQuery({
    queryKey: courseKeys.departmentAssignments(courseId),
    queryFn: async () => {
      const response = await coursesApi.getDepartmentAssignments(courseId);
      return response.data!;
    },
    enabled: !!courseId,
  });
};

export const useEnrollDepartment = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');
  return useMutation({
    mutationFn: ({
      courseId,
      departmentId,
      data,
    }: {
      courseId: string;
      departmentId: string;
      data?: AssignDepartmentRequest;
    }) => coursesApi.enrollDepartment(courseId, departmentId, data),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.departmentAssignments(courseId) });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      addToast({
        title: t('courses.assignDepartments.enrollSuccess'),
        color: 'success',
      });
    },
    onError: (error) => {
      addToast({
        title: error.message || t('courses.assignDepartments.enrollError'),
        color: 'danger',
      });
    },
  });
};

export const useUnenrollDepartment = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');
  return useMutation({
    mutationFn: ({ courseId, departmentId }: { courseId: string; departmentId: string }) =>
      coursesApi.unenrollDepartment(courseId, departmentId),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.departmentAssignments(courseId) });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      addToast({
        title: t('courses.assignDepartments.unenrollSuccess'),
        color: 'success',
      });
    },
    onError: (error) => {
      addToast({
        title: error.message || t('courses.assignDepartments.unenrollError'),
        color: 'danger',
      });
    },
  });
};

export const useCurrentUserEnrollment = (courseId: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: courseKeys.currentUserEnrollment(courseId, user?.id || ''),
    queryFn: async () => {
      if (!user?.id) return null;
      const response = await coursesApi.getCurrentUserEnrollment(courseId);
      return response.data;
    },
    enabled: !!courseId && !!user?.id,
  });
};

export const useCurrentUserProgress = (courseId: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: courseKeys.currentUserProgress(courseId, user?.id || ''),
    queryFn: async () => {
      if (!user?.id) return null;
      const response = await coursesApi.getCurrentUserProgress(courseId);
      return response.data;
    },
    enabled: !!courseId && !!user?.id,
  });
};

export const useUpdateCurrentUserProgress = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({
      courseId,
      data,
    }: {
      courseId: string;
      data: {
        isCompleted?: boolean;
      };
    }) => coursesApi.updateCurrentUserProgress(courseId, data),
    onSuccess: (_, { courseId }) => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: courseKeys.currentUserProgress(courseId, user.id) });
        queryClient.invalidateQueries({ queryKey: courseKeys.userResumeCourses('en') });
        queryClient.invalidateQueries({ queryKey: courseKeys.userResumeCourses('es') });
      }
    },
  });
};

export const useCourseGuidesProgress = (courseId: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: courseKeys.guidesProgress(courseId, user?.id || ''),
    queryFn: () => coursesApi.getCourseGuidesProgress(courseId),
    enabled: !!courseId && !!user?.id,
    select: (response) => response.data,
  });
};

export const useSelfEnroll = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useTranslation('courses');
  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: string; data?: EnrollUserRequest }) =>
      coursesApi.selfEnroll(courseId, data),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.enrollments(courseId) });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: courseKeys.currentUserEnrollment(courseId, user.id) });
        queryClient.invalidateQueries({ queryKey: courseKeys.currentUserProgress(courseId, user.id) });
      }
      addToast({
        title: t('enrollSuccess') || 'Successfully enrolled in course',
        color: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: error.message || t('enrollError') || 'Failed to enroll in course',
        color: 'danger',
      });
    },
  });
};

export const useSelfUnenroll = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useTranslation('courses');
  return useMutation({
    mutationFn: ({ courseId }: { courseId: string }) => coursesApi.selfUnenroll(courseId),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.enrollments(courseId) });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: courseKeys.currentUserEnrollment(courseId, user.id) });
        queryClient.invalidateQueries({ queryKey: courseKeys.currentUserProgress(courseId, user.id) });
      }
      addToast({
        title: t('unenrollSuccess') || 'Successfully unenrolled from course',
        color: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: error.message || t('unenrollError') || 'Failed to unenroll from course',
        color: 'danger',
      });
    },
  });
};

export const useInfiniteUserResumeCourses = (language: string) => {
  return useInfiniteQuery({
    queryKey: courseKeys.userResumeCourses(language),
    queryFn: async ({ pageParam }) => {
      const response = await coursesApi.getUserResumeCourses({
        language: language,
        cursor: pageParam,
        limit: 10,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: !!language,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useInfiniteUserRecommendedCourses = (language: string) => {
  return useInfiniteQuery({
    queryKey: courseKeys.userRecommendedCourses(language),
    queryFn: async ({ pageParam }) => {
      const response = await coursesApi.getRecommendedCourses({
        language: language,
        cursor: pageParam,
        limit: 6,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: !!language,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useInfiniteUserAssignedCourses = (language: string) => {
  return useInfiniteQuery({
    queryKey: courseKeys.userAssignedCourses(language),
    queryFn: async ({ pageParam }) => {
      const response = await coursesApi.getUserAssignedCourses({
        language: language,
        cursor: pageParam,
        limit: 10,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: !!language,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
